import React, { useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Loader2, CheckCircle2, AlertCircle } from "lucide-react";
import { motion } from "framer-motion";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";

/**
 * Verify page — final step in the magic-link flow.
 *
 * Why this page waits for a user click before consuming the token
 * ----------------------------------------------------------------
 * Corporate email security tools like Darktrace Antigena, Microsoft
 * Defender SafeLinks, Proofpoint URL Defense, Mimecast etc. pre-fetch
 * every link in incoming email — often in a headless browser that
 * executes JavaScript. If we auto-fire the POST on page load, those
 * scanners burn the single-use token before the real user ever clicks.
 * That's exactly the "link has already been used" failure we kept
 * seeing for buyers on corporate/edu domains.
 *
 * The defense is industry-standard: require a real user gesture (a
 * mouse / touch click on a button) before calling the verify endpoint.
 * Headless scanners virtually never simulate full pointer events, so
 * the token survives the pre-fetch and is only consumed when the
 * actual buyer interacts with the page.
 */
export default function Verify() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [status, setStatus] = useState("ready");
  const [error, setError] = useState("");
  const token = params.get("token");

  const consume = async () => {
    if (!token) {
      setStatus("error");
      setError("No token provided.");
      return;
    }
    setStatus("loading");
    try {
      await api.post(`/auth/verify-magic-link?token=${encodeURIComponent(token)}`);
      await refresh();
      setStatus("ok");
      setTimeout(() => navigate("/library"), 700);
    } catch (e) {
      setStatus("error");
      setError(e?.response?.data?.detail || "This link can't be used.");
    }
  };

  return (
    <div className="App min-h-screen flex items-center justify-center" data-testid="verify-page">
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center px-6 max-w-md">
        {status === "ready" && (
          <div data-testid="verify-ready">
            <p className="text-[11px] font-bold uppercase tracking-[0.22em] mb-4" style={{ color: "var(--mek-text-3)" }}>
              Manage(Meant) Excellence
            </p>
            <p className="font-serif text-3xl mb-3" style={{ color: "var(--mek-text)" }}>
              One last click.
            </p>
            <p className="text-prose mb-8">
              For security, your toolkit opens when you click below — not automatically.
            </p>
            <button
              onClick={consume}
              className="btn-primary"
              data-testid="verify-open-btn"
            >
              Open my toolkit
            </button>
          </div>
        )}
        {status === "loading" && (
          <>
            <Loader2 className="mx-auto animate-spin mb-6" size={28} style={{ color: "var(--mek-accent)" }} />
            <p className="font-serif text-2xl">Opening your toolkit…</p>
          </>
        )}
        {status === "ok" && (
          <>
            <CheckCircle2 className="mx-auto mb-6" size={32} style={{ color: "#4A613F" }} />
            <p className="font-serif text-2xl">You're in.</p>
            <p className="text-prose mt-2">Taking you to your library…</p>
          </>
        )}
        {status === "error" && (
          <div data-testid="verify-error">
            <AlertCircle className="mx-auto mb-6" size={28} style={{ color: "#8B3E3E" }} />
            <p className="font-serif text-2xl mb-3">This link can't be used.</p>
            <p className="text-prose mb-8">{error}</p>
            <button className="btn-primary" onClick={() => navigate("/access")}>
              Request a new link
            </button>
          </div>
        )}
      </motion.div>
    </div>
  );
}
